import { Theme, useMediaQuery } from "@mui/material";
import {
  CreateButton,
  DateField,
  ExportButton,
  FilterButton,
  FunctionField,
  ListContextProvider,
  ReferenceManyCount,
  SelectColumnsButton,
  TextField,
  TopToolbar,
  useListController,
} from "react-admin";
import { ChunkedExportButton } from "@/components/common/ChunkedExportButton";
import { useEffect, useState } from "react";
import { ListViewButton } from "./components/ListViewButton";
import DesktopGrid from "./components/DesktopGrid";
import MobileGrid from "./components/MobileGrid";
import { useListView } from "./components/useListView";
import { ClientListAside } from "./components/ClientListAside";
import { StatusChipField } from "@/components/common/fields/StatusChipField";
import { getProps } from "@/utils/helpers/columnsHelper";
import { CreateInDialog } from "@/components/common/CreateInDialog";
import { ResourceName, type Client, type ClientCreate } from "@/types";
import List from "@/components/ui/react-admin/styledList/StyledList";
import { PageHeader } from "@/components/common/PageHeader";
import { getResourceIcon } from "@/utils/icons";
import { StyledDatagridConfigurable } from "@/components/ui/react-admin/StyledDatagrid";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { ListEmptyState } from "@/components/common/ListEmptyState";
import { ActivePolicyCell } from "./components/ActivePolicyCell";
import { isNonProductionEnvironment } from "@/utils/environment";
import { jsonExporter } from "@/utils/exporters/jsonExporter";
import { Download as DownloadIcon } from "@mui/icons-material";

export const ClientList = () => {
  const isXsmall = useMediaQuery<Theme>((theme) => theme.breakpoints.down("sm"));
  const [view] = useListView();
  const isSmall = useMediaQuery<Theme>((theme) => theme.breakpoints.down("md"));

  // Explicit default sort preserves the prior server order (newest first) now
  // that the API honors sort. Without it react-admin would default to id ASC.
  const listContext = useListController({
    resource: ResourceName.CLIENTS,
    sort: { field: "created_datetime", order: "DESC" },
  });

  const { isPending: loading } = listContext;

  const ClientIcon = getResourceIcon(ResourceName.CLIENTS);

  return (
    <>
      <ListContextProvider value={listContext}>
        <PageHeader
          customTitle="Clients"
          hideSectionTitle
          rightContent={<ClientListActions loading={loading} />}
          headerIcon={<ClientIcon sx={{ fontSize: 28, color: "primary.main" }} />}
        />
        <List
          filters={isSmall ? clientFilters : undefined}
          perPage={25}
          sort={{ field: "created_datetime", order: "DESC" }}
          aside={<ClientListAside />}
          actions={false}
          loading={loading}
          empty={<ListEmptyState resource={ResourceName.CLIENTS} />}
        >
          {isXsmall ? (
            <MobileGrid />
          ) : view === "grid" ? (
            <DesktopGrid />
          ) : (
            <StyledDatagridConfigurable
              rowClick="show"
              sx={{
                "& .column-groups": {
                  md: { display: "none" },
                  lg: { display: "table-cell" },
                },
              }}
              omit={defaultOmittedFields}
            >
              {columns}
            </StyledDatagridConfigurable>
          )}
        </List>
      </ListContextProvider>
    </>
  );
};

export const clientFilters: React.ReactElement[] = [];

const ClientListActions = ({ loading }: { loading?: boolean }) => {
  const isSmall = useMediaQuery<Theme>((theme) => theme.breakpoints.down("md"));
  const [view] = useListView();
  const [showFilter, setShowFilter] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const { canCreate } = useResourcePermissions(ResourceName.CLIENTS);

  // When resizing manually the window to go from large to small, the useMediaQuery used here
  // triggers its change before the one in the VisitorList. This leads to an error because the
  // FilterButton requires the filters prop to be set.
  useEffect(() => {
    // Needed to avoid react warnings
    let isMounted = true;
    setTimeout(() => {
      if (isMounted) setShowFilter(isSmall);
    }, 0);
    return () => {
      isMounted = false;
    };
  }, [isSmall]);

  return (
    <TopToolbar>
      {canCreate && (
        <>
          <CreateButton
            onClick={(e) => {
              e.preventDefault();
              setOpenCreateDialog(true);
            }}
            disabled={loading}
          />
          <CreateInDialog<ClientCreate>
            open={openCreateDialog}
            onClose={() => setOpenCreateDialog(false)}
            resource={ResourceName.CLIENTS}
          />
        </>
      )}
      {showFilter ? <FilterButton /> : null}
      {view === "table" ? <SelectColumnsButton disabled={loading} /> : null}
      {/* Chunked export: fetches the full partner-scoped client list in 1000-row
          pages (no single large request) and truncates loudly at 50000 (#5166). */}
      <ChunkedExportButton disabled={loading} />
      {isNonProductionEnvironment() && (
        <ExportButton
          label="Export JSON"
          icon={<DownloadIcon />}
          maxResults={50000}
          exporter={(records) => jsonExporter(records, ResourceName.CLIENTS)}
          disabled={loading}
        />
      )}
      <ListViewButton disabled={loading} />
    </TopToolbar>
  );
};

const columns = [
  // Core Client Info
  <TextField {...getProps("aegle_id", ResourceName.CLIENTS)} />,
  <TextField {...getProps("client_legal_name", ResourceName.CLIENTS)} />,
  <StatusChipField {...getProps("client_status", ResourceName.CLIENTS)} />,
  <FunctionField
    source="has_active_policy"
    label="Policy"
    render={(record: Client) => <ActivePolicyCell clientId={record?.id} />}
  />,
  <TextField {...getProps("client_short_name", ResourceName.CLIENTS)} />,

  <ReferenceManyCount
    link
    sortable={false}
    reference="claimants"
    target="client_id_ref"
    label={getProps("calculations.claimant_count", ResourceName.CLIENTS).label}
  />,
  <ReferenceManyCount
    link
    sortable={false}
    reference="stoploss-claims"
    target="client_id_ref"
    label={getProps("calculations.claim_count", ResourceName.CLIENTS).label}
  />,
  <TextField {...getProps("client_state_headquartered", ResourceName.CLIENTS)} />,

  // Business Status
  <TextField {...getProps("client_parent_co", ResourceName.CLIENTS)} />,

  // Audit Fields
  <DateField {...getProps("created_datetime")} />,
  <TextField {...getProps("created_by")} />,
  <DateField {...getProps("modified_datetime")} showTime />,
  <TextField {...getProps("modified_by")} />,
];

// ... rest of file unchanged ...

// Fields to hide by default (users can still show them via column selector)
const defaultOmittedFields = [
  "aegle_id",
  "client_short_name",
  "client_parent_co",
  "created_by",
  "modified_datetime",
  "modified_by",
];
